import { ADD_CAMERA_STATUS_TEXTS } from '@hunting-nature/Secacam/pages/add-camera-flow/add-camera/add-camera.config';
import type { ADD_CAMERA_ERROR } from '@hunting-nature/shared-core/enums/add-camera-error.enum';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';

import { isLegacyCamera } from '../../../shared/helper/remote-control.helper';
import { createScopedSelector } from '../../../shared/utils/factory.utils';

import { AddCameraFlowActions } from './add-camera-flow.actions';
import type { CameraModelsJson } from '@hunting-nature/shared-core/interfaces/camera-models.interface';

export const ADD_CAMERA_FLOW_STORE_KEY = 'addCameraFlow';

export interface TestImageStatus {
  cameraId: string;
  requested: boolean;
  imageUrl?: string;
  finished: boolean;
  retryCount: number;
}

export interface AddCameraFlowState {
  currentStep: number;
  activatedCameras: Array<Camera>;
  isFlowActive: boolean;
  addingCamera: boolean;
  addCameraStatusTextIndex: number;
  addCameraError: ADD_CAMERA_ERROR | undefined;
  showAddCameraForm: boolean; // Controls form vs list view on AddCameraPage
  scannedActivationCode: string | undefined; // Stores scanned code from OCR

  gpsActivationPending: boolean;
  gpsActivationSucceeded: boolean;
  gpsActivationFailed: boolean;

  testImageStatuses: Record<string, TestImageStatus>;
  testImagesQueryStarted: boolean;

  cameraModels: CameraModelsJson | undefined;

  returnUrl: string | undefined;
}

export const initialAddCameraFlowState: AddCameraFlowState = {
  currentStep: 1,
  activatedCameras: [],
  isFlowActive: false,
  addingCamera: false,
  addCameraStatusTextIndex: 0,
  addCameraError: undefined,
  showAddCameraForm: true,
  scannedActivationCode: undefined,

  gpsActivationPending: false,
  gpsActivationSucceeded: false,
  gpsActivationFailed: false,

  testImageStatuses: {},
  testImagesQueryStarted: false,

  cameraModels: undefined,

  returnUrl: undefined,
};

export const addCameraFlowReducer = createReducer(
  initialAddCameraFlowState,
  on(AddCameraFlowActions.openAddCameraFlow, (state: AddCameraFlowState, { returnUrl }): AddCameraFlowState => {
    return {
      ...state,
      currentStep: 1,
      activatedCameras: [],
      isFlowActive: true,
      addingCamera: false,
      addCameraError: undefined,
      showAddCameraForm: true,
      scannedActivationCode: undefined,
      returnUrl,
    };
  }),
  on(AddCameraFlowActions.closeAddCameraFlow, (): AddCameraFlowState => {
    return initialAddCameraFlowState;
  }),

  on(AddCameraFlowActions.addCameraFlowEnteredSucceeded, (state: AddCameraFlowState, action): AddCameraFlowState => {
    return {
      ...state,
      cameraModels: action.cameraModels,
      addCameraStatusTextIndex: 0,
    };
  }),

  on(AddCameraFlowActions.goToTestConnectionStep, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      currentStep: 2,
    };
  }),
  on(AddCameraFlowActions.goBackFromTestConnectionStep, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      currentStep: 1,
    };
  }),
  on(AddCameraFlowActions.goToGpsSetupStep, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      currentStep: state.activatedCameras.every(item => isLegacyCamera(item.model)) ? 4 : 3,
    };
  }),
  on(AddCameraFlowActions.goBackFromGpsSetupStep, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      currentStep: 2,
    };
  }),
  on(AddCameraFlowActions.goToSetupCompleteStep, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      currentStep: 4,
    };
  }),
  on(AddCameraFlowActions.goBackFromSetupCompleteStep, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      currentStep: state.activatedCameras.every(item => isLegacyCamera(item.model)) ? 2 : 3,
    };
  }),

  on(AddCameraFlowActions.showAddCameraForm, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      showAddCameraForm: true,
    };
  }),
  on(AddCameraFlowActions.scanActivationCodeSucceeded, (state: AddCameraFlowState, action): AddCameraFlowState => {
    return {
      ...state,
      scannedActivationCode: action.activationCode,
    };
  }),
  on(AddCameraFlowActions.scanActivationCodeFailed, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      scannedActivationCode: undefined,
    };
  }),
  on(AddCameraFlowActions.addCamera, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      addingCamera: true,
      addCameraError: undefined,
      scannedActivationCode: undefined, // Clear scanned code when adding camera
    };
  }),
  on(AddCameraFlowActions.addCameraSucceeded, (state: AddCameraFlowState, { camera }): AddCameraFlowState => {
    return {
      ...state,
      activatedCameras: [...state.activatedCameras, camera],
      addingCamera: false,
      addCameraError: undefined,
      showAddCameraForm: false, // Switch to list view after adding
      addCameraStatusTextIndex: 0,
    };
  }),
  on(AddCameraFlowActions.addCameraFailed, (state: AddCameraFlowState, action): AddCameraFlowState => {
    return {
      ...state,
      addingCamera: false,
      addCameraError: action.error,
      addCameraStatusTextIndex: 0,
    };
  }),
  on(AddCameraFlowActions.addCameraChangeStatusText, (state: AddCameraFlowState, action): AddCameraFlowState => {
    return {
      ...state,
      addCameraStatusTextIndex: action.index,
    };
  }),
  on(AddCameraFlowActions.activateGpsForAllCameras, (state: AddCameraFlowState, _action): AddCameraFlowState => {
    return {
      ...state,
      gpsActivationPending: true,
      gpsActivationSucceeded: false,
      gpsActivationFailed: false,
    };
  }),
  on(AddCameraFlowActions.activateCameraGpsSucceeded, (state: AddCameraFlowState, _action): AddCameraFlowState => {
    return {
      ...state,
      gpsActivationPending: false,
      gpsActivationSucceeded: true,
      gpsActivationFailed: false,
    };
  }),
  on(AddCameraFlowActions.activateCameraGpsFailed, (state: AddCameraFlowState, _action): AddCameraFlowState => {
    return {
      ...state,
      gpsActivationPending: false,
      gpsActivationSucceeded: false,
      gpsActivationFailed: true,
    };
  }),
  on(AddCameraFlowActions.requestTestImageForAllNewCameras, (state: AddCameraFlowState, action): AddCameraFlowState => {
    const windows = { ...state.testImageStatuses };

    action.cameraIds.forEach(id => {
      windows[id] = {
        cameraId: id,
        requested: false,
        finished: false,
        retryCount: 0,
      };
    });

    return {
      ...state,
      testImageStatuses: windows,
      testImagesQueryStarted: true,
    };
  }),
  on(AddCameraFlowActions.requestTestImageForNewCamera, (state: AddCameraFlowState, action): AddCameraFlowState => {
    return {
      ...state,
      testImageStatuses: {
        ...state.testImageStatuses,
        [action.cameraId]: {
          ...state.testImageStatuses[action.cameraId],
          requested: true,
        },
      },
    };
  }),
  on(AddCameraFlowActions.requestTestImageForNewCameraSuccess, (state: AddCameraFlowState, action): AddCameraFlowState => {
    return {
      ...state,
      testImageStatuses: {
        ...state.testImageStatuses,
        [action.cameraId]: {
          ...state.testImageStatuses[action.cameraId],
          imageUrl: action.imageUrl,
          finished: true,
        },
      },
    };
  }),
  on(AddCameraFlowActions.requestTestImageForNewCameraFailed, (state: AddCameraFlowState, action): AddCameraFlowState => {
    const finished = state.testImageStatuses[action.cameraId].retryCount >= action.maximumNumberOfRetries;
    const retryCount = state.testImageStatuses[action.cameraId].retryCount + (finished ? 0 : 1);

    return {
      ...state,
      testImageStatuses: {
        ...state.testImageStatuses,
        [action.cameraId]: {
          ...state.testImageStatuses[action.cameraId],
          finished: finished,
          retryCount: retryCount,
        },
      },
    };
  }),
  on(AddCameraFlowActions.restartRequestTestImageForNewCamera, (state: AddCameraFlowState, action): AddCameraFlowState => {
    return {
      ...state,
      testImageStatuses: {
        ...state.testImageStatuses,
        [action.cameraId]: {
          ...state.testImageStatuses[action.cameraId],
          requested: true,
          imageUrl: undefined,
          finished: false,
          retryCount: 0,
        },
      },
      testImagesQueryStarted: true,
    };
  }),
  on(AddCameraFlowActions.requestTestImageForAllNewCamerasFinished, (state: AddCameraFlowState): AddCameraFlowState => {
    return {
      ...state,
      testImagesQueryStarted: false,
    };
  })
);

const selectAddCameraFlowState = createFeatureSelector<AddCameraFlowState>(ADD_CAMERA_FLOW_STORE_KEY);
const createAddCameraFlowSelector = createScopedSelector(selectAddCameraFlowState);
const testImageStatuses = createAddCameraFlowSelector((state: AddCameraFlowState) => state.testImageStatuses);

export const AddCameraFlowSelectors = {
  currentStep: createAddCameraFlowSelector((state: AddCameraFlowState) => state.currentStep),
  returnUrl: createAddCameraFlowSelector((state: AddCameraFlowState) => state.returnUrl),
  activatedCameras: createAddCameraFlowSelector((state: AddCameraFlowState) => state.activatedCameras),
  activatedCamerasCount: createAddCameraFlowSelector((state: AddCameraFlowState) => state.activatedCameras.length),
  activatedCamerasId: createAddCameraFlowSelector((state: AddCameraFlowState) => {
    const cameraIds: Array<string> = [];
    state.activatedCameras.forEach((camera: Camera) => {
      cameraIds.push(camera.id);
    });

    return cameraIds;
  }),
  testImageStatuses,
  isAllTestImageFinished: createSelector(testImageStatuses, (_testImageStatuses: Record<string, TestImageStatus>) => {
    const images: Array<TestImageStatus> = Object.values(_testImageStatuses);
    if (images.length === 0) {
      return false;
    }

    return images.every((testImage: TestImageStatus) => testImage.finished);
  }),
  atLeastOneTestImageSuccessfulFinished: createSelector(testImageStatuses, (_testImageStatuses: Record<string, TestImageStatus>) => {
    const images: Array<TestImageStatus> = Object.values(_testImageStatuses);
    if (images.length === 0) {
      return false;
    }

    return images.some((testImage: TestImageStatus) => testImage.imageUrl !== undefined && testImage.finished);
  }),
  isFlowActive: createAddCameraFlowSelector((state: AddCameraFlowState) => state.isFlowActive),
  isTestImagesQueryStarted: createAddCameraFlowSelector((state: AddCameraFlowState) => state.testImagesQueryStarted),
  isAddingCamera: createAddCameraFlowSelector((state: AddCameraFlowState) => state.addingCamera),
  addCameraError: createAddCameraFlowSelector((state: AddCameraFlowState) => state.addCameraError),
  showAddCameraForm: createAddCameraFlowSelector((state: AddCameraFlowState) => state.showAddCameraForm),
  scannedActivationCode: createAddCameraFlowSelector((state: AddCameraFlowState) => state.scannedActivationCode),
  addCameraStatusTextKey: createAddCameraFlowSelector((state: AddCameraFlowState) => ADD_CAMERA_STATUS_TEXTS[state.addCameraStatusTextIndex]),

  isGpsActivationPending: createAddCameraFlowSelector((state: AddCameraFlowState) => state.gpsActivationPending),
  isGpsActivationDefault: createAddCameraFlowSelector(
    (state: AddCameraFlowState) => !state.gpsActivationPending && !state.gpsActivationSucceeded && !state.gpsActivationFailed
  ),
  isGpsActivationSucceeded: createAddCameraFlowSelector((state: AddCameraFlowState) => state.gpsActivationSucceeded),
  isGpsActivationFailed: createAddCameraFlowSelector((state: AddCameraFlowState) => state.gpsActivationFailed),
  cameraModelName: (model: string | undefined) =>
    createAddCameraFlowSelector((state: AddCameraFlowState) => {
      const models: CameraModelsJson | undefined = state.cameraModels;
      if (!models) {
        return '';
      }

      return ((model ? models.knownModels[model] : undefined) ?? models.unknownModel).name;
    }),
  cameraModelImage: (model: string | undefined) =>
    createAddCameraFlowSelector((state: AddCameraFlowState) => {
      const models: CameraModelsJson | undefined = state.cameraModels;
      if (!models) {
        return '';
      }

      return ((model ? models.knownModels[model] : undefined) ?? models.unknownModel).image;
    }),
};
